import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";

export type ViewportMode = "mobile" | "desktop";

export const DESKTOP_MIN_WIDTH_PX = 1024;
export const DESKTOP_MEDIA_QUERY = `(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`;

const MOBILE_USER_AGENT_REGEX =
  /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;

/**
 * Desktop mode = web browser (not the Capacitor app), not a phone user agent,
 * and viewport >= 1024px. Re-evaluated live on resize / media-query change.
 *
 * Product decision: tablets are treated by width. iPadOS Safari reports a desktop
 * (Mac) user agent, so an iPad in landscape (>= 1024px) gets the desktop UI and in
 * portrait gets the mobile UI — same as a narrow/wide desktop browser window.
 */

const isNativeApp = (): boolean => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export const isMobileUserAgent = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }
  const navWithUaData = navigator as Navigator & {
    userAgentData?: { mobile?: boolean };
  };
  if (typeof navWithUaData.userAgentData?.mobile === "boolean") {
    return navWithUaData.userAgentData.mobile;
  }
  return MOBILE_USER_AGENT_REGEX.test(navigator.userAgent || "");
};

const computeNaturalDesktopMatch = (): boolean => {
  if (typeof window === "undefined" || isNativeApp() || isMobileUserAgent()) {
    return false;
  }
  if (typeof window.matchMedia === "function") {
    const mql = window.matchMedia(DESKTOP_MEDIA_QUERY);
    if (mql && typeof mql.matches === "boolean") {
      return mql.matches;
    }
    return false;
  }
  return window.innerWidth >= DESKTOP_MIN_WIDTH_PX;
};

export interface UseViewportModeResult {
  mode: ViewportMode;
  isDesktop: boolean;
  isMobile: boolean;
  isDesktopCapableWeb: boolean;
}

export function useViewportMode(): UseViewportModeResult {
  const [naturalDesktop, setNaturalDesktop] = useState<boolean>(() =>
    computeNaturalDesktopMatch(),
  );

  useEffect(() => {
    if (typeof window === "undefined" || isNativeApp()) {
      return;
    }

    const mql =
      typeof window.matchMedia === "function"
        ? window.matchMedia(DESKTOP_MEDIA_QUERY)
        : null;

    const handleViewportUpdate = () => {
      setNaturalDesktop(computeNaturalDesktopMatch());
    };

    handleViewportUpdate();

    if (mql) {
      if (typeof mql.addEventListener === "function") {
        mql.addEventListener("change", handleViewportUpdate);
      } else if (typeof mql.addListener === "function") {
        mql.addListener(handleViewportUpdate);
      }
    }

    window.addEventListener("resize", handleViewportUpdate);

    return () => {
      if (mql) {
        if (typeof mql.removeEventListener === "function") {
          mql.removeEventListener("change", handleViewportUpdate);
        } else if (typeof mql.removeListener === "function") {
          mql.removeListener(handleViewportUpdate);
        }
      }
      window.removeEventListener("resize", handleViewportUpdate);
    };
  }, []);

  const mode: ViewportMode =
    isNativeApp() || !naturalDesktop ? "mobile" : "desktop";

  return {
    mode,
    isDesktop: mode === "desktop",
    isMobile: mode === "mobile",
    isDesktopCapableWeb: mode === "desktop",
  };
}
